import { format, utcToZonedTime } from 'date-fns-tz';
import { isToday, isFuture, isPast, compareAsc, formatISO, toDate } from 'date-fns';

const localFullDateFormatterOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric'
};

const time24FormatterOptions = {
  hour: 'numeric',
  minute: 'numeric',
  hour12: false
};

const localTimeFormatterOptions = {
  hour: 'numeric',
  minute: 'numeric',
  hour12: !!(new Date().toLocaleTimeString().match(/am|pm/i))
};


export enum DateFnsFormats {
  NumericDateTime12Hours = 'dd/MM/yy h:mm a',
  StringifiedDayMonthNumericDateYear = 'EEEE, MMMM d, yyyy',
  FullDate = 'yyyy-MM-dd HH:mm:ss.sssz',
  StringifiedFullDayMonthNumericDateYear = 'EEEE, MMMM dd, yyyy',
  StringifiedShortDay = 'EEEE MM/dd',
  StringifiedShortFullDateDay12Hours = 'EE MM/dd/yyy',
  StringifiedShortFullDateDay24Hours = 'EE dd/MM/yyy',
  ShortDayDate = 'MM/dd',
}

const defaultLocalRegion = 'default';
const enUsRegion = 'en-us';

export const getCurrentTimezone = ():string => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const getIsToday = (date: string):boolean => isToday(new Date(date));

export const getIsPast = (date: string):boolean => isPast(new Date(date));

export const getIsFuture = (date: string):boolean => isFuture(new Date(date));

export const getCompareAsc = (prev: string | Date, next: string | Date):number => compareAsc(new Date(prev), new Date(next));

export const convertStringToDate = (date: string):Date => toDate(new Date(date));

export const getStartCurrentISODay = ():string => {
  const time = new Date();
  time.setHours(0, 0, 0, 0);
  return formatISO(time);
};

export const getStartISODay = (date: string):string => {
  const time = new Date(date);
  time.setHours(0, 0, 0, 0);
  return formatISO(time);
};

export const dateToFormattedString = (date: string | Date, dateFormat: string, anyTimezone?: string): string => {
  const dateToRender = anyTimezone ? utcToZonedTime(date, anyTimezone) : date;

  return format(dateToRender, dateFormat, anyTimezone ? { timeZone: anyTimezone } : undefined);
};

export const getFullDateInLocalFormat = (date: string | Date, timezone: string): string => {
  return utcToZonedTime(date, timezone).toLocaleDateString(defaultLocalRegion, localFullDateFormatterOptions);
};

export const getFullDateInUsEuFormat = (date: string | Date, timezone: string): string => {
  return utcToZonedTime(date, timezone).toLocaleDateString(enUsRegion, localFullDateFormatterOptions);
};

export const getFullLocalDate = (date: string | Date, timezone: string): Date => {
  return utcToZonedTime(date, timezone);
};

export const getTimeInLocalFormat = (date: string | Date, timezone: string): string => {
  return utcToZonedTime(date, timezone).toLocaleTimeString(defaultLocalRegion, localTimeFormatterOptions).replace(' ','');
};

export const getTimeIn24Format = (date: string | Date, timezone: string): string => {
  return utcToZonedTime(date, timezone).toLocaleTimeString(defaultLocalRegion, time24FormatterOptions).replace(':','');
};

export const getFormatTimezone = (dateTime: Date, timeZone?: string):string => {
  //Get timezone in +00 format
  const formatRegex = /^[+-][0]/;
  const timeZoneShort = timeZone ? format(dateTime,'xxx' ,{ timeZone }) : format(dateTime,'xxx' );
  //Remove trailing zero and negative sign
  const formattedTimezone = timeZoneShort.replace(formatRegex, `${timeZoneShort[0]}`);
  return `GMT ${formattedTimezone}`;
};

/**
 * Return current DateTime in ISO without ms
 */
export const getDateTimeIsoOmitMs = (): string => (new Date()).toISOString().split('.')[0] + 'Z';
